import { useDispatch, useSelector } from "react-redux";
import { setPageTitle } from "@/store/pageTitleSlice";
import toast from "react-hot-toast";
import {
  buildLabeledText,
  copyTextToClipboard,
  isCopyableValue as checkCopyableValue,
} from "@/lib/clipboard";

export default function useUtility() {
  const showAmount = (
    amount = 0,
    currencyFormat = true,
    decimal = 2,
    separate = true,
    exceptZeros = false,
  ) => {
    const currencyFormatSetting = 3; // 1=>Both 2=>Text Only 3=>Symbol Only
    const currencySymbol = gs("cur_sym");
    const currencyText = gs("cur_text");
    let separator = "";

    if (separate) {
      separator = ",";
    }

    let printAmount = parseFloat(amount)
      .toFixed(decimal)
      .replace(/\d(?=(\d{3})+\.)/g, "$&" + separator);

    if (exceptZeros) {
      let parts = printAmount.split(".");

      if (parseInt(parts[1]) === 0) {
        printAmount = parts[0];
      } else {
        printAmount = printAmount.replace(/0+$/, "");
      }
    }

    if (currencyFormat) {
      if (currencyFormatSetting === 1) {
        return currencySymbol + printAmount + " " + currencyText;
      }

      if (currencyFormatSetting === 2) {
        return printAmount + " " + currencyText;
      }

      return currencySymbol + printAmount;
    }

    return printAmount;
  };

  const settings = useSelector((state) => state.gs.data);
  const gsStatus = useSelector((state) => state.gs.status);

  const gs = (key) => {
    if (gsStatus != "succeeded") return 99;

    return settings?.data?.general_setting?.[key];
  };

  const language = useSelector((state) => state.language.data);
  const langStatus = useSelector((state) => state.language.status);

  const trans = (key) => {
    if (langStatus === "loading") return "";

    if (langStatus === "succeeded") {
      return language?.data?.file?.[key] ?? key;
    } else {
      return "";
    }
  };

  const dispatch = useDispatch();
  const pageTitle = (title) => {
    dispatch(setPageTitle(title));
  };

  // Add a number formatting helper
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const num = Number(value.toString().replace(/,/g, ""));
    if (isNaN(num)) return "0.00";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const copyToClipboard = async (value, label = "", options = {}) => {
    const {
      emptyMessage = "Nothing to copy",
      successMessage,
      errorMessage = "Failed to copy",
      showToast = true,
    } = options;

    if (!checkCopyableValue(value)) {
      if (showToast) toast.error(trans(emptyMessage));
      return false;
    }

    const result = await copyTextToClipboard(value);
    if (result.ok) {
      if (showToast) {
        toast.success(
          trans(
            successMessage ??
              (label ? `${label} copied to clipboard` : "Copied to clipboard"),
          ),
        );
      }
      return true;
    }

    if (showToast) toast.error(trans(errorMessage));
    return false;
  };

  const copyLabeledEntries = async (entries, options = {}) => {
    const {
      successMessage = "Copied to clipboard",
      emptyMessage = "Nothing to copy",
      errorMessage = "Failed to copy",
      showToast = true,
    } = options;

    const text = buildLabeledText(entries);
    if (!text.trim()) {
      if (showToast) toast.error(trans(emptyMessage));
      return false;
    }

    const result = await copyTextToClipboard(text);
    if (result.ok) {
      if (showToast) toast.success(trans(successMessage));
      return true;
    }

    if (showToast) toast.error(trans(errorMessage));
    return false;
  };

  return {
    showAmount,
    trans,
    gs,
    pageTitle,
    formatNumberWithCommas,
    isCopyableValue: checkCopyableValue,
    copyToClipboard,
    copyLabeledEntries,
  };
}
